// BundleCarousel.js
/**
 * BundleCarousel - Handles bundle display in two contexts:
 * 
 * 1. PRODUCT PAGE MODE (dynamic):
 *    - Variant changes based on user selection
 *    - Bundles update when variant changes
 *    - Listens to variant change events
 * 
 * 2. CART MODE (static):
 *    - Uses fixed variant from cart item (data-variant-id)
 *    - Bundles never change - locked to cart item's variant
 *    - Ignores all variant change events
 */
class BundleCarousel {
  constructor(container) {
    console.log('🚀 NEW BundleCarousel initializing...');
    console.log('🔍 Container element:', container);
    console.log('🔍 Container dataset:', container.dataset);
    console.log('🔍 data-variant-id:', container.dataset.variantId);
    console.log('🔍 data-product-id:', container.dataset.productId);
    console.log('🔍 data-bundle-id:', container.dataset.bundleId);
    
    this.container = container;
    this.bundleId = container.dataset.bundleId;
    this.bundleColumns = container.querySelectorAll('.bundle-column');
    this.hiddenItemsData = {};
    
    // Determine if this is a cart context (fixed variant) or product page context (dynamic variant)
    this.isCartContext = !!container.dataset.variantId;
    
    console.log('🚀 Initializing BundleCarousel for:', this.bundleId);
    console.log('📍 Context:', this.isCartContext ? 'CART (fixed variant)' : 'PRODUCT PAGE (dynamic variant)');

    try {
      const dataElement = container.querySelector(`.variant-bundle-data[data-bundle-id="${this.bundleId}"]`);
      this.bundleData = JSON.parse(dataElement?.textContent || '{}');
      console.log('📊 Bundle data loaded:', Object.keys(this.bundleData).length, 'variants');
    } catch (e) {
      console.error('❌ JSON Parse Error:', e);
      return;
    }

    this.currentVariantId = this.getCurrentVariantId();
    console.log('🎯 Locked to variant ID:', this.currentVariantId);
    this.init();
  }

  getCurrentVariantId() {
    console.log('🔍 getCurrentVariantId() called');
    console.log('🔍 isCartContext:', this.isCartContext);
    
    if (this.isCartContext) {
      // Cart context: Use the fixed variant ID from the cart item
      const cartVariantId = this.container.dataset.variantId;
      console.log('🛒 CART MODE: Container variant ID:', cartVariantId);
      console.log('🛒 CART MODE: Using fixed variant ID:', cartVariantId);
      return cartVariantId;
    }

    // Product page context: Detect current variant dynamically
    console.log('📄 PRODUCT PAGE MODE: Detecting current variant...');
    
    const urlParams = new URLSearchParams(window.location.search);
    const variantFromUrl = urlParams.get('variant');
    console.log('🔍 URL variant:', variantFromUrl);
    if (variantFromUrl) {
      console.log('✅ Using URL variant ID:', variantFromUrl);
      return variantFromUrl;
    }

    const variantInput = document.querySelector('input[name="id"]');
    console.log('🔍 Input variant:', variantInput?.value);
    if (variantInput?.value) {
      console.log('✅ Using input variant ID:', variantInput.value);
      return variantInput.value;
    }

    const variantSelect = document.querySelector('select[name="id"]');
    console.log('🔍 Select variant:', variantSelect?.value);
    if (variantSelect?.value) {
      console.log('✅ Using select variant ID:', variantSelect.value);
      return variantSelect.value;
    }

    const fallback = Object.keys(this.bundleData)[0];
    console.log('🔍 Fallback variant ID:', fallback);
    return fallback;
  }

  formatPrice(priceInCents) {
    if (!priceInCents || isNaN(priceInCents)) return '0.00';
    return (parseFloat(priceInCents) / 100).toFixed(2);
  }

  init() {
    // Load bundles for the determined variant
    this.loadBundleForVariant(this.currentVariantId);
    
    // Only bind variant change listeners for product page context
    if (!this.isCartContext) {
      this.bindVariantChange();
      console.log('📄 Variant change listeners bound for product page');
    } else {
      console.log('🛒 CART MODE: Skipping variant change listeners - bundles are fixed');
    }
  }

  createAccordionStructure(components, bundleColumnId) {
    const needsAccordion = components.length > 3;
    
    if (!needsAccordion) {
      return components.map(component => this.createComponentHTML(component, bundleColumnId)).join('');
    }

    // Create HTML for ALL components, but mark which ones should be hidden initially
    let allItemsHTML = '';
    
    components.forEach((component, index) => {
      const isHidden = index >= 3;
      const hiddenClass = isHidden ? ' accordion-hidden-item' : '';
      const hiddenStyle = isHidden ? ' style="display: none;"' : '';
      
      allItemsHTML += `
        <div class="bundle-component-item${hiddenClass}" data-variant-id="${component.id}" data-price="${component.price}" data-bundle-column="${bundleColumnId}"${hiddenStyle}>
          <div class="component-image">
            <img src="${component.image || ''}" alt="${component.title}" loading="lazy">
          </div>
          <div class="component-right">
            <div class="component-top">
              <div class="component-titles">
                <h4><a href="/products/${component.handle}">${component.product_title}</a></h4>
                ${component.has_multiple_variants ? `<p class="variant-title">${component.title}</p>` : ''}
              </div>
              <p class="component-price">$${this.formatPrice(component.price)}</p>
            </div>
            <button type="button" class="toggle-button bundle-toggle active" data-variant-id="${component.id}" data-bundle-column="${bundleColumnId}">
              <span class="toggle-label">Remove</span>
            </button>
          </div>
        </div>
      `;
    });

    // Add the accordion toggle button
    allItemsHTML += `
      <div class="bundle-accordion-toggle" data-bundle-column="${bundleColumnId}">
        <div class="accordion__toggle bold">
          <div class="text-with-icon">
            <span>See all items (${components.length})</span>
          </div>
          <span class="circle-chevron">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </div>
      </div>
    `;

    return allItemsHTML;
  }

  createComponentHTML(component, bundleColumnId) {
    return `
      <div class="bundle-component-item" data-variant-id="${component.id}" data-price="${component.price}" data-bundle-column="${bundleColumnId}">
        <div class="component-image">
          <img src="${component.image || ''}" alt="${component.title}" loading="lazy">
        </div>
        <div class="component-right">
          <div class="component-top">
            <div class="component-titles">
              <h4><a href="/products/${component.handle}">${component.product_title}</a></h4>
              ${component.has_multiple_variants ? `<p class="variant-title">${component.title}</p>` : ''}
            </div>
            <p class="component-price">$${this.formatPrice(component.price)}</p>
          </div>
          <button type="button" class="toggle-button bundle-toggle active" data-variant-id="${component.id}" data-bundle-column="${bundleColumnId}">
            <span class="toggle-label">Remove</span>
          </button>
        </div>
      </div>
    `;
  }

  loadBundleForVariant(variantId) {
    console.log('🔍 loadBundleForVariant() called with:', variantId);
    console.log('🔍 isCartContext:', this.isCartContext);
    console.log('🔍 Available bundle data keys:', Object.keys(this.bundleData));
    console.log('🔍 Bundle data for variant ' + variantId + ':', this.bundleData[variantId]);
    
    if (this.isCartContext) {
      console.log('🛒 CART MODE: Loading fixed bundles for cart item variant:', variantId);
    } else {
      console.log('📄 PRODUCT PAGE: Loading bundles for selected variant:', variantId);
    }
    
    const bundleData = this.bundleData[variantId] || {};
    const bundles = bundleData.bundles || [];
    
    console.log('🔍 Found bundles:', bundles.length);
    if (bundles.length > 0) {
      console.log('🔍 Bundle titles:', bundles.map(b => b.bundle_title));
    }

    this.bundleColumns.forEach((column, index) => {
      const title = column.querySelector('.bundle-dynamic-title');
      const image = column.querySelector('.bundle-dynamic-image');
      const list = column.querySelector('.bundle-components-list');
      const description = column.querySelector('.bundle_description');

      // Clear existing content
      if (title) title.textContent = '';
      if (image) {
        image.removeAttribute('src');
        image.removeAttribute('alt');
      }
      if (list) list.innerHTML = '';

      const bundle = bundles[index];
      console.log(`🔍 Bundle ${index + 1}:`, bundle?.bundle_title, '- Components:', bundle?.bundle_components?.length || 0);

      if (bundle?.bundle_components?.length > 0) {
        console.log(`✅ Showing bundle ${index + 1}: ${bundle.bundle_title}`);
        column.style.display = '';
        
        if (title) title.textContent = bundle.bundle_title || '';
        if (description) description.textContent = bundle.bundle_description || '';
        if (image && bundle.featured_image) {
          image.setAttribute('src', bundle.featured_image);
          image.setAttribute('alt', bundle.bundle_title);
        }

        if (list) {
          const bundleColumnId = column.dataset.bundleColumnId;
          list.innerHTML = this.createAccordionStructure(bundle.bundle_components, bundleColumnId);

          setTimeout(() => {
            this.bindToggleButtonsForColumn(column);
            this.bindAccordionForColumn(column);
            this.updateColumnTotal(bundleColumnId);
          }, 10);
        }
      } else {
        console.log(`❌ Hiding bundle ${index + 1} - no components`);
        column.style.display = 'none';
      }
    });

    const anyVisible = Array.from(this.bundleColumns).some(col => col.style.display !== 'none');
    const heading = this.container.querySelector('.bundle-heading');
    if (heading) heading.style.display = anyVisible ? '' : 'none';

    if (this.isCartContext) {
      console.log('🛒 CART MODE: Bundles loaded and fixed - will not change');
    } else {
      console.log('📄 PRODUCT PAGE: Bundles loaded - will update with variant changes');
    }

    setTimeout(() => {
      this.bindAddToCartButtons();
    }, 20);
  }

  bindAccordionForColumn(column) {
    const accordionToggles = column.querySelectorAll('.bundle-accordion-toggle');
    accordionToggles.forEach(toggle => {
      // Remove any existing event listeners
      const newToggle = toggle.cloneNode(true);
      toggle.parentNode.replaceChild(newToggle, toggle);
      
      newToggle.addEventListener('click', (e) => {
        e.preventDefault();
        const bundleColumnId = newToggle.dataset.bundleColumn;
        const hiddenItems = column.querySelectorAll('.accordion-hidden-item');
        const chevron = newToggle.querySelector('.circle-chevron');
        const textSpan = newToggle.querySelector('.text-with-icon span');
        
        const isExpanded = newToggle.classList.contains('expanded');
        
        if (isExpanded) {
          // Collapse: hide items and reset chevron
          hiddenItems.forEach(item => item.style.display = 'none');
          newToggle.classList.remove('expanded');
          if (chevron) chevron.style.transform = 'rotate(0deg)';
          
          // Get total items count for the original text
          const allItems = column.querySelectorAll('.bundle-component-item');
          if (textSpan) textSpan.textContent = `See all items (${allItems.length})`;
        } else {
          // Expand: show hidden items
          hiddenItems.forEach(item => item.style.display = '');
          newToggle.classList.add('expanded');
          if (chevron) chevron.style.transform = 'rotate(180deg)';
          if (textSpan) textSpan.textContent = 'See less items';
        }
      });
    });
  }

  bindToggleButtonsForColumn(column) {
    const buttons = column.querySelectorAll('.bundle-toggle');
    buttons.forEach(button => {
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);

      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = newButton.dataset.variantId;
        const bundleColumn = newButton.dataset.bundleColumn;
        newButton.classList.toggle('active');
        const isActive = newButton.classList.contains('active');
        const label = newButton.querySelector('.toggle-label');
        if (label) label.textContent = isActive ? 'Remove' : 'Add';
        newButton.style.backgroundColor = '#bb2f28';
        newButton.style.borderColor = '#bb2f28';
        newButton.style.color = 'white';
        newButton.setAttribute('title', isActive ? 'Click to remove from bundle' : 'Click to add to bundle');
        this.updateColumnTotal(bundleColumn);
      });
    });
  }

  updateColumnTotal(bundleColumnId) {
    const column = this.container.querySelector(`[data-bundle-column-id="${bundleColumnId}"]`);
    const totalElement = column?.querySelector('.bundle-total');
    if (!totalElement) return;
    
    // Get active buttons from both visible and hidden items
    const activeButtons = column.querySelectorAll('.bundle-toggle.active');
    let total = 0;
    
    activeButtons.forEach(btn => {
      const componentItem = btn.closest('.bundle-component-item');
      const price = parseInt(componentItem?.dataset.price || '0', 10);
      if (!isNaN(price)) total += price;
    });
    
    totalElement.textContent = this.formatPrice(total);
  }

  bindAddToCartButtons() {
    const buttons = this.container.querySelectorAll('.add-bundle-to-cart');
    buttons.forEach(button => {
      const newBtn = button.cloneNode(true);
      button.parentNode.replaceChild(newBtn, button);
      newBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const bundleColumnId = newBtn.dataset.bundleColumnId;
        const column = this.container.querySelector(`[data-bundle-column-id="${bundleColumnId}"]`);
        
        // Get ALL active buttons (both visible and hidden)
        const activeButtons = column.querySelectorAll('.bundle-toggle.active');
        console.log('🛒 ADD TO CART - Bundle Column ID:', bundleColumnId);
        console.log('🛒 ADD TO CART - Found active buttons:', activeButtons.length);
        
        // Debug: Show all buttons found
        activeButtons.forEach((btn, index) => {
          const item = btn.closest('.bundle-component-item');
          const productTitle = item.querySelector('.component-titles h4 a').textContent;
          const isHidden = item.classList.contains('accordion-hidden-item');
          console.log(`🛒 Item ${index + 1}: ${productTitle} (Hidden: ${isHidden}) - Variant ID: ${btn.dataset.variantId}`);
        });
        
        const items = Array.from(activeButtons).map(btn => ({
          id: parseInt(btn.dataset.variantId, 10),
          quantity: 1
        }));
        
        console.log('🛒 ADD TO CART - Items to add:', items);
        
        if (!items.length) return alert('Please select at least one item from this bundle.');

        newBtn.disabled = true;
        const originalText = newBtn.textContent;
        newBtn.textContent = 'Adding...';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items })
          });
          if (!res.ok) throw await res.json();
          document.dispatchEvent(new CustomEvent('variant:add', { detail: { items } }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          newBtn.textContent = 'Added!';
          setTimeout(() => {
            newBtn.disabled = false;
            newBtn.textContent = originalText;
          }, 2000);
        } catch (error) {
          alert(error?.message || 'Error adding items to cart.');
          newBtn.disabled = false;
          newBtn.textContent = originalText;
        }
      });
    });

    // Add bundle *product* button
    const bundleProductButtons = this.container.querySelectorAll('.add-bundle-product-to-cart');
    bundleProductButtons.forEach(button => {
      const newBtn = button.cloneNode(true);
      button.parentNode.replaceChild(newBtn, button);

      newBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = parseInt(newBtn.dataset.variantId, 10);
        const title = newBtn.dataset.productTitle;
        const lineItemKey = newBtn.dataset.lineItemKey;
        
        // ADD DEBUGGING:
        console.log('🔍 BUTTON DEBUG:');
        console.log('🔍 Button element:', newBtn);
        console.log('🔍 Button dataset:', newBtn.dataset);
        console.log('🔍 data-line-item-key:', newBtn.dataset.lineItemKey);
        console.log('🔍 lineItemKey variable:', lineItemKey);
        console.log('🔍 Has line item key?', !!lineItemKey);
        
        const isUpgrade = !!lineItemKey; // If we have a line item key, this is an upgrade

        if (isNaN(variantId)) {
          alert('Invalid variant ID');
          return;
        }

        console.log('🛒 Bundle Product Action:', isUpgrade ? 'UPGRADE' : 'ADD');
        console.log('🛒 Bundle Product Variant ID:', variantId);
        if (isUpgrade) {
          console.log('🛒 Removing line item key:', lineItemKey);
        }

        newBtn.disabled = true;
        const originalText = newBtn.textContent;
        newBtn.textContent = isUpgrade ? 'Upgrading...' : 'Adding...';

        try {
          // Step 1: Remove existing item if this is an upgrade
          if (isUpgrade && lineItemKey) {
            console.log('🛒 Step 1: Removing current item...');
            const removeRes = await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({
                updates: {
                  [lineItemKey]: 0
                }
              })
            });
            
            if (!removeRes.ok) {
              const removeError = await removeRes.json();
              throw new Error(removeError.message || 'Failed to remove current item');
            }
            
            console.log('🛒 ✅ Current item removed successfully');
          }

          // Step 2: Add the bundle product
          console.log('🛒 Step 2: Adding bundle product...');
          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }]
            })
          });

          if (!addRes.ok) {
            const addError = await addRes.json();
            throw new Error(addError.message || 'Failed to add bundle product');
          }

          console.log('🛒 ✅ Bundle product added successfully');

          // Dispatch events to refresh cart
          document.dispatchEvent(new CustomEvent('variant:add', { detail: { items: [{ id: variantId }] } }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));

          newBtn.textContent = isUpgrade ? 'Upgraded!' : 'Added!';
          
          // Close cart drawer after upgrade (optional)
          if (isUpgrade) {
            setTimeout(() => {
              // You might want to close the cart drawer here
              // document.dispatchEvent(new CustomEvent('cart:close'));
            }, 1500);
          }
          
          setTimeout(() => {
            newBtn.disabled = false;
            newBtn.textContent = originalText;
          }, 2000);

        } catch (err) {
          console.error('🛒 ❌ Error during bundle product action:', err);
          alert(err?.message || `Couldn't ${isUpgrade ? 'upgrade to' : 'add'} ${title}`);
          newBtn.disabled = false;
          newBtn.textContent = originalText;
        }
      });
    });
  }

  bindVariantChange() {
    // This method should only be called for product page context
    console.log('📄 PRODUCT PAGE: Binding variant change listeners');

    document.addEventListener('variant:change', (event) => {
      const variantId = event.detail?.variant?.id;
      if (variantId && variantId.toString() !== this.currentVariantId) {
        console.log('📄 PRODUCT PAGE: Variant changed to:', variantId);
        this.currentVariantId = variantId.toString();
        this.loadBundleForVariant(this.currentVariantId);
      }
    });

    document.querySelectorAll('select[name="id"], input[name="id"]').forEach(input => {
      input.addEventListener('change', (e) => {
        const variantId = e.target.value;
        if (variantId && variantId !== this.currentVariantId) {
          console.log('📄 PRODUCT PAGE: Variant selector changed to:', variantId);
          this.currentVariantId = variantId;
          setTimeout(() => this.loadBundleForVariant(variantId), 10);
        }
      });
    });
  }
}

function initializeBundleCarousels(container = document) {
  console.log('🔄 Initializing bundle carousels in:', container);
  const sections = container.querySelectorAll('.bundle_component_section');
  console.log('🔍 Found', sections.length, 'bundle sections');
  
  sections.forEach((section, index) => {
    console.log(`🎯 Initializing section ${index + 1}:`, section.dataset.bundleId);
    try {
      new BundleCarousel(section);
    } catch (error) {
      console.error('❌ Error initializing bundle carousel:', error);
    }
  });
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('📄 DOM Content Loaded - Initializing bundles');
    initializeBundleCarousels();
  });
} else {
  console.log('📄 Document already ready - Initializing bundles');
  initializeBundleCarousels();
}

// Handle Shopify section reloads
document.addEventListener('shopify:section:load', (e) => {
  console.log('🔄 Shopify section loaded - Re-initializing bundles');
  initializeBundleCarousels(e.target);
});

// Add event listener for cart drawer opening (adjust this selector to match your cart drawer)
document.addEventListener('cart:open', () => {
  console.log('🛒 Cart opened - Re-initializing bundles');
  setTimeout(() => {
    initializeBundleCarousels();
  }, 100);
});

// Alternative cart drawer event listeners (use whichever matches your setup)
document.addEventListener('drawer:open', () => {
  console.log('🛒 Drawer opened - Re-initializing bundles');
  setTimeout(() => {
    initializeBundleCarousels();
  }, 100);
});

// Global exposure
window.BundleCarousel = BundleCarousel;
window.initializeBundleCarousels = initializeBundleCarousels;